import clone from 'clone'
import { isApplianceMessage, isConfigMessage, isDeviceMessage, isFlagMessage, isUpdateMessage, SocketMessage } from '../types/messaging'
import { Device, Port, ID, DeviceHwID, Rule, DevicePortSpec, DeviceValues } from '../types/models'
import { isRelayPort, PortNumber, PortType } from '../types/ports'
import { Safety } from './Safety'
import { isDebug } from '../utils/misc'

export type DeviceCollectionFilter = {
  deviceId?: ID
  deviceHwId?: DeviceHwID
  portType?: PortType | PortType[]
  locationId?: ID
  portId?: ID
  connected?: boolean
  enabledOnly?: boolean
} | false | 'ALL'

export type PortUpdate = {
  port_number: PortNumber
  name?: string
  rule_id?: ID
}

export type DeviceUpdate = {
  hw_id: DeviceHwID
  port?: PortUpdate
  name?: string
  comments?: string
}

/**
 * A precalculated collection of certain kind of devices extracted from the application context.
 */
export class DeviceCollection {

  devices: Device[]

  constructor() {
    this.devices = []
    this.loadState()
  }

  /**
   * Load the device group state from the local store.
   */
  loadState(): void {
    // This runs when the module loads, also on data cached by a previous build that
    // `adoptSessionForNewBuild()` has not replaced yet. Failing here would leave a
    // blank page on every load, so start empty instead.
    try {
      this.devices = Safety.get('devices', [])
      this.refreshLinks()
    } catch (err) {
      console.error(err)
      this.devices = []
    }
  }

  /**
   * Save the current device group state to the local store.
   */
  saveState(): void {
    Safety.set('devices', this.devices.length ? this.devices : undefined)
  }

  /**
   * Set the full device list state.
   */
  setDevices(devices: Device[]): void {
    this.devices = devices
    this.saveState()
    this.refreshLinks()
  }

  /**
   * Recalculate lookup tables and full port IDs.
   */
  refreshLinks() {
    for (const device of this.devices || []) {
      const portSpecs: Record<PortNumber, DevicePortSpec> = {}

      if (device.device_spec) {
        device.device_spec.params.ports.forEach(spec => {
          portSpecs[spec.port_number] = spec
        })
      }
      for (const port of device.ports || []) {
        port.device_id = device.id
        port.spec = portSpecs[port.port_number] || {}
      }
    }
  }

  /**
   * Reselect list of devices from the state based on the filtering.
   */
  filter(filter: DeviceCollectionFilter): { ports: Port[], devices: Device[] } {
    let devices: Device[] = this.allDevices()
    let ports: Port[] = []

    if (filter === false) {
      devices = []
    } else if (filter === 'ALL') {
      devices = this.allDevices()
    } else if (filter.deviceId !== undefined) {
      const deviceId = filter.deviceId
      devices = this.allDevices().filter((dev: Device) => (dev.id === deviceId))
    } else if (filter.deviceHwId !== undefined) {
      const deviceId = filter.deviceHwId
      devices = this.allDevices().filter((dev: Device) => (dev.hw_id === deviceId))
    } else if (filter.locationId !== undefined) {
      const locationId: ID = filter.locationId
      devices = this.allDevices().filter((dev: Device) => (dev.location_id === locationId))
    } else if (filter.portId !== undefined) {
      const portId: number = parseInt(`${filter.portId}`)
      devices = this.allDevices().filter((dev: Device) => !!dev.ports?.map(port => port.id).includes(portId as ID))
      if (devices.length) {
        const port = devices[0].ports?.find(port => port.id === portId)
        if (port) {
          ports.push(port)
        }
      }
    } else if (filter.connected) {
      devices = this.allDevices().filter((dev: Device) => dev.ports && dev.ports?.filter(port => 'type' in port.config).length > 0)
    }

    if (filter && filter !== 'ALL' && filter.portType) {
      const portType: PortType[] = filter.portType instanceof Array ? filter.portType : [filter.portType]
      const filteredDevices: Device[] = []
      devices.forEach(device => {
        const matches = device.ports?.filter(p => portType.includes(p.type)) || []
        if (matches.length) {
          filteredDevices.push(device)
          ports = ports.concat(matches)
        }
      })
      devices = filteredDevices
    }

    if (!ports.length) {
      ports = devices.reduce<Port[]>((prev, curr) => prev.concat(curr.ports || []), [])
    }

    // Apply port specific filters.
    if (filter !== false && filter !== 'ALL') {
      if (filter.enabledOnly) {
        ports = ports.filter(port => {
          if (port.config.pending && 'enabled' in port.config.pending) {
            return port.config.pending.enabled
          }
          if ('enabled' in port.config) {
            return port.config.enabled
          }
          return false
        })
      }
      if (filter.connected) {
        ports = ports.filter(p => isRelayPort(p) && ('type' in p.config))
      }
    }

    return { devices, ports }
  }

  /**
   * Get all devices currently saved in the state.
   */
  allDevices(): Device[] {
    return this.devices
  }

  /**
   * Update device data from incoming message.
   */
  onMessage(msg: SocketMessage): void {
    let changed = false

    if (isUpdateMessage(msg)) {
      const port = this.findPortById(msg.device_id, msg.port_id)
      if (port) {
        port.values = port.values || {}
        Object.assign(port.values, msg.values)
        changed = true
      }
    } else if (isDeviceMessage(msg)) {
      const device = this.findDevice(msg.hw_id)
      if (device) {
        Object.assign(device.values as DeviceValues, msg.values)
        changed = true
      }
    } else if (isFlagMessage(msg)) {
      const port = this.findPortById(msg.device_id, msg.port_id)
      if (port) {
        port.flags = msg.flags
        changed = true
      }
    } else if (isConfigMessage(msg)) {
      const port = this.findPortById(msg.device_id, msg.port_id)
      if (port) {
        Object.assign(port.config, msg.config)
        // Pending is no longer cleared by incoming config messages.
        // It is only cleared explicitly via clearPendingConfig() (after flash, on cancel, etc.).
        changed = true
      }
    } else if (isApplianceMessage(msg)) {
      // This will be implemented later. For now, we just listen the messages where needed.
    } else {
      console.error(`No handler for message ${JSON.stringify(msg)}.`)
    }

    if (changed) {
      this.saveState()
    }
  }

  /**
   * Find a device.
   */
  findDevice(hwId: DeviceHwID): Device | undefined {
    for (const device of this.devices || []) {
      if (device.hw_id === hwId) {
        return device
      }
    }
  }

  /**
   * Find a device by its ID.
   */
  findDeviceById(id: ID): Device | undefined {
    for (const device of this.devices || []) {
      if (device.id === id) {
        return device
      }
    }
  }

  /**
   * Find a port for a device.
   */
  findPort(hwId: DeviceHwID, portNumber: PortNumber): Port | undefined {
    const device = this.findDevice(hwId)
    if (device) {
      for (const port of device.ports || []) {
        if (port.port_number === portNumber) {
          return port
        }
      }
    }
  }

  /**
   * Find a port for a device by their IDs.
   */
  findPortById(deviceId: ID, portId: ID): Port | undefined {
    const device = this.findDeviceById(deviceId)
    if (device) {
      for (const port of device.ports || []) {
        if (port.id === portId) {
          return port
        }
      }
    }
  }

  /**
   * Update function for other details than live Websocket data.
   */
  onUpdate(params: DeviceUpdate) {
    let changed = false
    if (params.port?.port_number) {
      const port = this.findPort(params.hw_id, params.port?.port_number)
      if (port) {
        Object.assign(port, params.port)
        changed = true
      }
    }
    if (params.name) {
      const dev = this.findDevice(params.hw_id)
      if (dev) {
        dev.name = params.name
        changed = true
      }
    }
    if (params.comments) {
      const dev = this.findDevice(params.hw_id)
      if (dev) {
        dev.comments = params.comments
        changed = true
      }
    }

    if (changed) {
      this.saveState()
    }
  }

  /**
   * Add new rule to the collection for a device. If rule exists, override the old.
   */
  addRule(hwId: DeviceHwID, rule: Rule) {
    const device = this.findDevice(hwId)
    if (!device) {
      throw new Error(`Cannot find a device with HW ID ${hwId}.`)
    }
    device.rules = device.rules || []
    const idx = device.rules.findIndex(r => r.id === rule.id)
    if (idx >= 0) {
      device.rules[idx] = clone(rule)
    } else {
      device.rules.push(clone(rule))
    }
    this.saveState()
  }

  /**
   * Scan all ports if they have any pending configurations.
   */
  havePendingConfig(): boolean {
    for (const device of this.devices || []) {
      for (const port of device.ports || []) {
        if (typeof port.config.pending === 'object' && Object.keys(port.config.pending).length > 0) {
          if (isDebug('log-pending')) console.log('PENDING:', port.port_number, port.config.pending)
          return true
        }
      }
    }

    return false
  }

  /**
   * Pending configurations of all ports that have any, keyed by `<hw id>/<port number>`.
   */
  pendingConfig(): Record<string, object> {
    const pending: Record<string, object> = {}
    for (const device of this.devices || []) {
      for (const port of device.ports || []) {
        if (typeof port.config.pending === 'object' && Object.keys(port.config.pending).length > 0) {
          pending[`${device.hw_id}/${port.port_number}`] = port.config.pending
        }
      }
    }
    return pending
  }

  /**
   * Remove all pending configurations.
   */
  clearPendingConfig(): void {
    for (const device of this.devices || []) {
      for (const port of device.ports || []) {
        if (port.config.pending) {
          port.config.pending = {}
        }
      }
    }

    this.saveState()
  }

}
